import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight, CalendarCheck, Mail, MapPin, MessageCircle, Phone, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { LogoHorizontal, LogoStacked } from "@/components/common/Logo";
import { Flag, LANGS } from "@/components/common/Flags";
import { OpenNow } from "@/components/home/HomeBlocks";
import { useConcierge } from "@/components/concierge/ConciergeContext";
import { useContent } from "@/lib/content";
import { useLang } from "@/lib/i18n";
import { HOTEL } from "@/lib/hotel";
import { srcSetFor } from "@/lib/images";
import { MENU_ITEMS } from "./nav";

const EASE = [0.2, 0.8, 0.2, 1];

/** Language as two flags with their names – used inside the menu where there is room for words */
export const LangFlags = ({ testid = "menu-language", className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center gap-1 ${className}`} role="group" aria-label="Language" data-testid={testid}>
      {LANGS.map((l) => {
        const active = lang === l.code;
        return (
          <button
            key={l.code}
            type="button"
            onClick={() => setLang(l.code)}
            aria-pressed={active}
            aria-label={l.label}
            data-testid={`${testid}-${l.code}`}
            className={`inline-flex h-10 items-center gap-2 rounded-md px-2.5 text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              active ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
            }`}
          >
            <Flag code={l.code} className="h-5 w-5" />
            <span className="hidden sm:inline">{l.label}</span>
          </button>
        );
      })}
    </div>
  );
};

const Tile = ({ to, href, onClick, icon: Icon, label, sub, testid }) => {
  const cls = "group flex min-h-[88px] flex-col justify-between gap-4 rounded-lg border border-border bg-card p-4 text-left transition-[border-color,background-color] duration-200 hover:border-foreground/30 hover:bg-secondary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  const body = (
    <>
      <span className="flex items-start justify-between gap-3">
        <Icon className="h-5 w-5 text-foreground" strokeWidth={1.75} aria-hidden />
        <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[0.9375rem] font-semibold leading-tight text-foreground tabular">{label}</span>
        <span className="mt-0.5 block break-words text-sm leading-snug text-muted-foreground">{sub}</span>
      </span>
    </>
  );
  if (to) return <Link to={to} onClick={onClick} className={cls} data-testid={testid}>{body}</Link>;
  if (href) return <a href={href} onClick={onClick} className={cls} data-testid={testid}>{body}</a>;
  return <button type="button" onClick={onClick} className={cls} data-testid={testid}>{body}</button>;
};

/**
 * Full-screen menu – the same on every screen size, opened from the «Meny» button in the bar.
 *  - left: the seven pages as large links, each with a one-line descriptor; hovering one swaps the
 *    photo on the right (desktop) so the menu reads as a map of the hotel, not a list of words
 *  - right: the photo, four utility tiles (my booking, phone, Fjordverten, e-mail), live restaurant
 *    status and the address
 *  - top: logo, language as flags, «Lukk»; bottom: one «Bestill opphald» button, always reachable
 */
export const MenuOverlay = ({ open, onOpenChange }) => {
  const { lang, tx } = useLang();
  const { data } = useContent();
  const { ask } = useConcierge();
  const reduce = useReducedMotion();
  const [hover, setHover] = useState(null);
  useEffect(() => { if (!open) setHover(null); }, [open]);

  const current = MENU_ITEMS.find((m) => m.id === hover) || MENU_ITEMS[0];
  const label = (m) => (lang === "en" ? m.en : m.nn);
  const desc = (m) => (lang === "en" ? m.den : m.dnn);
  const close = () => onOpenChange(false);
  const rise = (i) => (reduce ? {} : { initial: { opacity: 0, y: 14 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5, delay: 0.08 + i * 0.045, ease: EASE } });

  const tiles = [
    { to: "/mi-bestilling", onClick: close, icon: CalendarCheck, label: tx("Mi bestilling", "My booking"), sub: tx("Finn, endre eller avbestill", "Find, change or cancel"), testid: "header-my-booking-link" },
    { href: HOTEL.tel, icon: Phone, label: HOTEL.phoneDisplay, sub: tx("Ring resepsjonen", "Call reception"), testid: "menu-phone-link" },
    { onClick: () => { close(); ask(null); }, icon: MessageCircle, label: tx("Spør Fjordverten", "Ask Fjordverten"), sub: tx("Svarar døgnet rundt", "Answers around the clock"), testid: "menu-concierge-button" },
    { href: `mailto:${HOTEL.email}`, icon: Mail, label: tx("E-post", "E-mail"), sub: <><span className="sm:hidden">{tx("Send oss ein e-post", "Send us an e-mail")}</span><span className="hidden sm:inline">{HOTEL.email}</span></>, testid: "menu-email-link" },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="top" className="flex h-[100dvh] flex-col border-0 bg-background p-0 shadow-none [&>button]:hidden" data-testid="site-menu">
        <SheetTitle className="sr-only">{tx("Meny", "Menu")}</SheetTitle>
        <SheetDescription className="sr-only">{tx("Navigasjon og kontakt", "Navigation and contact")}</SheetDescription>

        {/* Top row – mirrors the bar so the menu feels like the bar unfolding, not a new page */}
        <div className="shrink-0 border-b border-border">
          <div className="mx-auto flex h-16 w-full max-w-site items-center justify-between px-5 sm:px-8 lg:h-20 lg:px-10">
            <Link to="/" onClick={close} className="inline-flex items-center text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={`${HOTEL.name} – ${tx("heim", "home")}`} data-testid="menu-logo-link">
              <LogoStacked className="h-8 sm:hidden" />
              <LogoHorizontal className="hidden h-[22px] sm:block lg:h-6" />
            </Link>
            <div className="flex items-center gap-1 sm:gap-4">
              <LangFlags />
              <button
                type="button"
                onClick={close}
                className="-mr-2 inline-flex h-10 items-center gap-2 rounded-md px-2.5 text-[0.9375rem] font-semibold text-foreground transition-colors duration-200 hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={tx("Lukk menyen", "Close menu")}
                data-testid="mobile-menu-close"
              >
                <span className="hidden sm:inline">{tx("Lukk", "Close")}</span>
                <X className="h-[22px] w-[22px]" strokeWidth={1.75} />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto grid w-full max-w-site grid-cols-12 gap-x-8 px-5 pb-10 pt-4 sm:px-8 lg:gap-x-12 lg:px-10 lg:pb-14 lg:pt-10">
            <nav className="col-span-12 lg:col-span-6" aria-label={tx("Hovudmeny", "Main menu")} data-testid="menu-primary-nav" onMouseLeave={() => setHover(null)}>
              <ul>
                {MENU_ITEMS.map((m, i) => (
                  <motion.li key={m.id} {...rise(i)} className="border-b border-border">
                    <NavLink
                      to={m.to}
                      end={m.to === "/"}
                      onClick={close}
                      onMouseEnter={() => setHover(m.id)}
                      onFocus={() => setHover(m.id)}
                      data-testid={`mobile-nav-${m.id}`}
                      className="group flex items-center justify-between gap-6 py-3.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:py-4 lg:py-[1.125rem]"
                    >
                      {({ isActive }) => (
                        <>
                          <span className="flex min-w-0 items-center gap-3.5">
                            <span className={`block text-[1.75rem] font-semibold leading-none tracking-[-0.03em] transition-colors duration-200 sm:text-[2rem] lg:text-[2.625rem] ${hover && hover !== m.id ? "text-foreground/45" : "text-foreground"}`}>{label(m)}</span>
                            {isActive && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-foreground" aria-hidden data-testid={`mobile-nav-${m.id}-current`} />}
                          </span>
                          <span className="flex shrink-0 items-center gap-3">
                            <span className={`hidden text-right text-sm transition-colors duration-200 sm:block lg:text-[0.9375rem] ${hover && hover !== m.id ? "text-muted-foreground/50" : "text-muted-foreground"}`}>{desc(m)}</span>
                            <ArrowUpRight className="h-5 w-5 text-muted-foreground transition-[transform,color] duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground lg:hidden" aria-hidden />
                          </span>
                        </>
                      )}
                    </NavLink>
                  </motion.li>
                ))}
              </ul>
            </nav>

            <div className="col-span-12 mt-8 lg:col-span-5 lg:col-start-8 lg:mt-0">
              {/* Preview photo – desktop only; follows the hovered/focused row */}
              <motion.div {...rise(1)} className="relative hidden aspect-[4/3] overflow-hidden rounded-2xl bg-muted lg:block" data-testid="menu-preview" data-item={current.id}>
                {MENU_ITEMS.map((m) => (
                  <img
                    key={m.id}
                    src={m.img}
                    srcSet={srcSetFor(m.img)}
                    sizes="(min-width: 1024px) 36vw, 1px"
                    alt=""
                    loading={m.id === "home" ? "eager" : "lazy"}
                    decoding="async"
                    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ease-out ${current.id === m.id ? "opacity-100" : "opacity-0"}`}
                    style={{ objectPosition: m.pos }}
                  />
                ))}
                <div className="scrim-hero-soft pointer-events-none absolute inset-x-0 bottom-0 h-1/2" aria-hidden />
                <p className="hero-shadow pointer-events-none absolute inset-x-6 bottom-5 text-white">
                  <span className="block text-sm text-white/75">{label(current)}</span>
                  <span className="mt-0.5 block text-[1.0625rem] font-semibold leading-snug">{desc(current)}</span>
                </p>
              </motion.div>

              <motion.div {...rise(3)} className="grid grid-cols-2 gap-3 lg:mt-5" data-testid="menu-utilities">
                {tiles.map((t) => <Tile key={t.testid} {...t} />)}
              </motion.div>

              <motion.div {...rise(5)} className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <OpenNow dining={data?.dining} testid="menu-open-now" />
                <a href={HOTEL.mapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="menu-address-link">
                  <MapPin className="h-4 w-4" strokeWidth={1.75} aria-hidden /> {HOTEL.address}
                </a>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Bottom – the one action that matters, always reachable without scrolling */}
        <div className="shrink-0 border-t border-border bg-background">
          <div className="mx-auto flex w-full max-w-site items-center justify-between gap-6 px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-8 lg:px-10 lg:py-4">
            <p className="hidden text-sm text-muted-foreground sm:block" data-testid="menu-promise">{tx("Frukost inkludert · betal når du kjem · gratis avbestilling til kl. 18.00 dagen før", "Breakfast included · pay on arrival · free cancellation until 18:00 the day before")}</p>
            <Button asChild size="lg" className="w-full rounded-md sm:w-auto" data-testid="mobile-menu-book-cta">
              <Link to="/bestill" onClick={close}>{tx("Bestill opphald", "Book your stay")} <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
